import React, { useState, useEffect } from 'react';
import { Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import postsAPI from '../services/postsAPI';

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ⚠️ SECURITY WARNING: This is a simple demonstration implementation.
  // For production use, implement proper backend authentication with:
  // - Secure password hashing (bcrypt, argon2)
  // - Session management or JWT tokens
  // - HTTPS only
  // - Rate limiting
  // - Password strength requirements
  const ADMIN_USERNAME = 'geomark';
  const ADMIN_PASSWORD = 'geomarkertiorisami';

  useEffect(() => {
    // Load posts from API or localStorage
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const fetchedPosts = await postsAPI.getPosts();
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setUsername('');
      setPassword('');
    } else {
      alert('არასწორი მომხმარებლის სახელი ან პაროლი!');
      setPassword('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Generate a more robust ID to avoid collisions
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 9);
      const id = `${timestamp}-${random}`;
      
      const newPost = {
        id,
        title,
        content,
        image: imageUrl,
        timestamp
      };

      // Save to API or localStorage
      await postsAPI.createPost(newPost);
      
      // Reload posts from API
      await loadPosts();

      // Reset form
      setTitle('');
      setContent('');
      setImageUrl('');
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        alert('საცავის ლიმიტი ამოიწურა! სურათი ძალიან დიდია. სცადეთ:\n1. უფრო პატარა სურათის გამოყენება\n2. ძველი პოსტების წაშლა\n3. სურათის URL-ის გამოყენება ატვირთვის ნაცვლად\n\nშენიშვნა: ბრაუზერის localStorage-ს აქვს ~5-10MB ლიმიტი ყველა მონაცემისთვის.');
      } else {
        alert('პოსტის შენახვა ვერ მოხერხდა: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ნამდვილად გსურთ ამ პოსტის წაშლა?')) {
      try {
        await postsAPI.deletePost(id);
        await loadPosts();
      } catch (error) {
        alert('პოსტის წაშლა ვერ მოხერხდა: ' + error.message);
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 2MB for better storage management)
      const maxSize = 2 * 1024 * 1024; // 2MB
      
      if (file.size > maxSize) {
        // Compress the image
        compressImage(file, (compressedDataUrl) => {
          setImageUrl(compressedDataUrl);
        });
      } else {
        // File is small enough, use as-is
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const compressImage = (file, callback) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions (max 800x800)
        let width = img.width;
        let height = img.height;
        const maxDimension = 800;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression (0.7 quality for JPEG)
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        callback(compressedDataUrl);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 text-center">ადმინის შესვლა</h2>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-slate-700 mb-2 font-medium">მომხმარებლის სახელი</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="შეიყვანეთ მომხმარებლის სახელი"
                required
                autoComplete="username"
              />
            </div>
            <div className="mb-4">
              <label className="block text-slate-700 mb-2 font-medium">პაროლი</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="შეიყვანეთ პაროლი"
                required
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition font-bold"
            >
              შესვლა
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-slate-600 hover:text-slate-800 flex items-center justify-center space-x-2"
          >
            <ArrowLeft size={20} />
            <span>მთავარ გვერდზე დაბრუნება</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h1 className="text-4xl font-bold text-slate-800">ადმინისტრატორის პანელი</h1>
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition w-full sm:w-auto"
            >
              <ArrowLeft size={20} />
              <span>მთავარ გვერდზე დაბრუნება</span>
            </button>
          </div>
        </div>

        {showSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
            პოსტი წარმატებით შეიქმნა!
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Create Post Form */}
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">ახალი პოსტის შექმნა</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-slate-700 mb-2 font-medium">სათაური</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="პოსტის სათაური"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-slate-700 mb-2 font-medium">შინაარსი</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 h-32"
                  placeholder="პოსტის შინაარსი"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-slate-700 mb-2 font-medium">სურათი</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  💡 რჩევა: 2MB-ზე მეტი სურათები ავტომატურად შემცირდება. საუკეთესო შედეგისთვის გამოიყენეთ 1MB-ზე ნაკლები სურათები.
                </p>
                {imageUrl && (
                  <img 
                    src={imageUrl} 
                    alt="Preview" 
                    className="mt-4 w-full h-48 object-cover rounded-lg"
                  />
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'პოსტი ინახება...' : 'პოსტის შექმნა'}
              </button>
            </form>
          </div>

          {/* Posts List */}
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">ყველა პოსტი ({posts.length})</h2>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {posts.length === 0 ? (
                <p className="text-slate-500 text-center py-8">ჯერ არ არის პოსტები. შექმენით თქვენი პირველი პოსტი!</p>
              ) : (
                posts.map(post => (
                  <div key={post.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition">
                    {post.image && (
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                    )}
                    <h3 className="font-bold text-slate-800 mb-1">{post.title}</h3>
                    <p className="text-sm text-slate-600 mb-2 line-clamp-2">{post.content}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-400">
                        {new Date(post.timestamp).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="text-red-500 hover:text-red-700 transition"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
